import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../../../assets/api/services/authApi';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { savePasscode } from '../../../assets/services/auth';
import PinDots from '../../PinPages/PinDots/PinDots';
import PinPage from '../../PinPages/PinPage/PinPage';
import PinPad from '../../PinPages/PinPad/PinPad';
import { usePinEntry } from '../../PinPages/usePinEntry/usePinEntry';
import './ProfilePasscodeChange.css';

const STEP = {
  CURRENT: 'current',
  NEW: 'new',
  CONFIRM: 'confirm',
};
const COPY_TRANSITION = {
  duration: 0.16,
  ease: [0.22, 1, 0.36, 1],
};
const PROCESSING_MS = 700;
const ERROR_PAUSE_MS = 360;
const CONFIRM_ERROR_PAUSE_MS = 360;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function stringifyApiErrorValue(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(stringifyApiErrorValue).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    return value.msg || value.message || value.detail || null;
  }
  return String(value);
}

function getApiErrorMessage(error, fallback) {
  return (
    stringifyApiErrorValue(error?.detail)
    || stringifyApiErrorValue(error?.payload?.detail)
    || stringifyApiErrorValue(error?.payload)
    || stringifyApiErrorValue(error?.message)
    || fallback
  );
}

function ProfilePasscodeChange({ authService = authApi }) {
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(CurrentUserContext);
  const { pin } = useContext(TranslationContext);
  const isMountedRef = useRef(true);
  const [step, setStep] = useState(STEP.CURRENT);
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorPulse, setErrorPulse] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showError = useCallback((message) => {
    setHasError(true);
    setErrorPulse((value) => value + 1);
    toast.error(message);
  }, []);

  const handleComplete = useCallback(
    async (value) => {
      setIsChecking(true);

      if (step === STEP.CURRENT) {
        try {
          await authService.checkPassword({ currentPassword: value });

          if (!isMountedRef.current) {
            return true;
          }

          setCurrentPasscode(value);
          setHasError(false);
          setIsChecking(false);
          setStep(STEP.NEW);
          return false;
        } catch (error) {
          await delay(ERROR_PAUSE_MS);

          if (!isMountedRef.current) {
            return false;
          }

          showError(getApiErrorMessage(error, pin.incorrectPasscode));
          setIsChecking(false);
          return false;
        }
      }

      if (step === STEP.NEW) {
        setNewPasscode(value);
        setHasError(false);
        setIsChecking(false);
        setStep(STEP.CONFIRM);
        return false;
      }

      if (value !== newPasscode) {
        await delay(CONFIRM_ERROR_PAUSE_MS);

        if (!isMountedRef.current) {
          return false;
        }

        showError(pin.passcodeMismatch);
        setIsChecking(false);
        return false;
      }

      setIsProcessing(true);
      await delay(PROCESSING_MS);

      try {
        await authService.updatePassword({
          currentPassword: currentPasscode,
          newPassword: newPasscode,
        });

        if (!isMountedRef.current) {
          return true;
        }

        savePasscode(newPasscode);
        setCurrentUser((prevUser) => {
          if (!prevUser) return prevUser;

          return {
            ...prevUser,
            reg_data: {
              ...prevUser.reg_data,
              stages: {
                ...prevUser.reg_data?.stages,
                is_password_set: true,
              },
            },
          };
        });
        toast.success(pin.changed);
        navigate('/profile/security', { replace: true });
        return true;
      } catch (error) {
        if (!isMountedRef.current) {
          return false;
        }

        showError(getApiErrorMessage(error, pin.updateFailed));
        setIsProcessing(false);
        setIsChecking(false);
        return false;
      }
    },
    [
      authService,
      currentPasscode,
      navigate,
      newPasscode,
      pin,
      setCurrentUser,
      showError,
      step,
    ]
  );

  const { digits, append, remove, reset } = usePinEntry(4, handleComplete);
  const isBusy = isChecking || isProcessing;

  useEffect(() => {
    reset();
    setHasError(false);
    setIsChecking(false);
    setIsProcessing(false);
  }, [reset, step]);

  const handleDigit = useCallback(
    (digit) => {
      if (hasError) {
        setHasError(false);
      }

      append(digit);
    },
    [append, hasError]
  );

  const handleBack = () => {
    if (isChecking || isProcessing) return;

    if (step === STEP.CONFIRM) {
      setStep(STEP.NEW);
      return;
    }

    if (step === STEP.NEW) {
      setNewPasscode('');
      setStep(STEP.CURRENT);
      return;
    }

    navigate('/profile/security');
  };

  const handleForgot = () => {
    if (isBusy) return;

    navigate('/forgot-pin', {
      state: {
        loggedInRecovery: true,
        backPath: '/profile/security/passcode',
      },
    });
  };

  const stepCopy = {
    [STEP.CURRENT]: {
      title: pin.currentTitle,
      subtitle: pin.currentSubtitle,
    },
    [STEP.NEW]: {
      title: pin.newTitle,
      subtitle: pin.newSubtitle,
    },
    [STEP.CONFIRM]: {
      title: pin.confirmNewTitle,
      subtitle: pin.confirmNewSubtitle,
    },
  };
  const copy = stepCopy[step];

  return (
    <PinPage className="profile-passcode-change">
      <div className="pin-page__body profile-passcode-change__body">
        <div className="pin-page__intro profile-passcode-change__intro">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={COPY_TRANSITION}
          >
            <h1 className="pin-page__title">{copy.title}</h1>
            <p className="pin-page__subtitle">{copy.subtitle}</p>
          </motion.div>
        </div>

        <PinDots
          length={4}
          filledCount={digits.length}
          hasError={hasError}
          errorKey={errorPulse}
          isProcessing={isProcessing && digits.length === 4 && !hasError}
          className="pin-dots--with-feedback"
        />
        <div className="profile-passcode-change__pad-group">
          {step === STEP.CURRENT && (
            <button
              type="button"
              className="profile-passcode-change__forgot"
              disabled={isBusy}
              onClick={handleForgot}
            >
              {pin.forgotPinButton}
            </button>
          )}
          <PinPad
            onDigit={handleDigit}
            onDelete={remove}
            onBack={handleBack}
            showDelete={digits.length > 0}
            disabled={isBusy}
          />
        </div>
      </div>
    </PinPage>
  );
}

export default ProfilePasscodeChange;
